import { useCallback, useEffect, useState } from 'react'

/**
 * Versions d'une page du builder : appels d'API et COMPARAISON de deux états.
 *
 * Logique pure, sans composant (un module .js, pas .jsx : l'exporter depuis un
 * fichier de composants casserait le Fast Refresh). Les écrans sont dans
 * BuilderVersions.jsx ; le serveur, dans PageVersionTrait.php.
 *
 * Une version arrive à la forme du `page.blocs` du canevas
 * (`{id, nid, type, data, hiddenOn}`), données déjà enrichies : la comparaison
 * travaille donc sur ce que le canevas rend, et l'identité d'un bloc est son
 * `id` (builderId) — stable d'une version à l'autre, y compris quand une
 * restauration recrée l'unité sous un autre nid.
 */

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

/** Diffusé quand l'historique change (version créée, restauration). */
export const VERSIONS_CHANGED_EVENT = 'builder:versions-changed'

const versionsUrl = (pageNid) => `/api/builder/page-versions/${encodeURIComponent(pageNid)}`

const readJson = async (res, fallback) => {
  const body = await res.json().catch(() => null)
  if (!res.ok) throw new Error(body?.message || `${fallback} (HTTP ${res.status})`)
  return body
}

/** Les versions de la page, la plus récente d'abord. */
export const fetchVersions = async (pageNid) => {
  const res = await fetch(versionsUrl(pageNid), { credentials: 'include', cache: 'no-store' })
  const body = await readJson(res, "Impossible de charger l'historique")
  return Array.isArray(body) ? body : []
}

/**
 * Une version prête à rendre — `{version, page}`. `vid = 'current'` : l'état
 * ENREGISTRÉ de la page, à la même forme (c'est lui qu'on compare à une
 * version quand on choisit « Version actuelle »).
 */
export const fetchVersion = async (pageNid, vid) => {
  const res = await fetch(`${versionsUrl(pageNid)}/${vid}`, { credentials: 'include', cache: 'no-store' })
  return readJson(res, 'Impossible de charger cette version')
}

/** Fige l'état enregistré de la page en une version. */
export const createVersion = async (pageNid, comment = '') => {
  const res = await fetch(versionsUrl(pageNid), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ comment }),
  })
  return readJson(res, "La version n'a pas pu être enregistrée")
}

/** Remet la page dans l'état d'une version (qui devient elle-même une version). */
export const restoreVersion = async (pageNid, vid) => {
  const res = await fetch(`${versionsUrl(pageNid)}/${vid}/restore`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: '{}',
  })
  return readJson(res, 'La restauration a échoué')
}

/** « 7 oct. 2026 à 14:32 » */
export const formatVersionDate = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const day = d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
  const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  return `${day} à ${time}`
}

// ---------------------------------------------------------------------------
// Égalité et texte
// ---------------------------------------------------------------------------

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v)

/**
 * « Rien » sous toutes ses formes. PHP décode `{}` en tableau vide et le
 * réencode `[]` ; « vider un champ » écrit `null` là où un bloc neuf n'a pas la
 * clé ; un texte effacé vaut `''`. Pour l'éditeur, c'est partout le même champ
 * vide — le compter comme un changement afficherait des différences fantômes.
 */
const isBlank = (v) => v == null || v === '' || (typeof v === 'object' && Object.keys(v).length === 0)

/** Égalité structurelle, « vide » compris (cf. isBlank). */
export const sameValue = (a, b) => {
  if (a === b) return true
  if (isBlank(a) && isBlank(b)) return true
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false
  if (Array.isArray(a) !== Array.isArray(b)) return false
  const ka = Object.keys(a)
  const kb = Object.keys(b)
  if (ka.length !== kb.length) return false
  return ka.every(k => sameValue(a[k], b[k]))
}

const looksLikeHtml = (s) => /<\/?[a-z][\s\S]*>/i.test(s)

/** Le texte lisible d'une valeur de champ texte / texte riche. */
const plainText = (value) => {
  let raw = value
  if (isObj(raw) && typeof raw.value === 'string') raw = raw.value
  if (typeof raw !== 'string') return null
  if (!looksLikeHtml(raw)) return raw
  // Paragraphes et retours : on garde la coupure, sinon deux paragraphes se
  // collent et le diff mot à mot les compare comme un seul mot.
  const spaced = raw.replace(/<\/(p|div|li|h[1-6])>|<br\s*\/?>/gi, '$&\n')
  if (typeof DOMParser !== 'undefined') {
    return (new DOMParser().parseFromString(spaced, 'text/html').body.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim()
  }
  return spaced.replace(/<[^>]+>/g, '').trim()
}

/** Texte comparable : espaces normalisés (insécables compris). */
const normText = (t) => String(t ?? '').replace(/[\u00a0\u202f]/g, ' ').replace(/\s+/g, ' ').trim()

/**
 * La signature de MISE EN FORME d'un texte riche : les balises qui changent
 * ce qu'on lit (gras, italique, lien et sa cible, titre, liste, citation), dans
 * l'ordre. Les enveloppes neutres (`p`, `div`, `span`, `br`) n'y figurent
 * pas : l'éditeur riche les ajoute ou les retire sans que rien ne change.
 */
const markup = (value) => {
  let raw = value
  if (isObj(raw) && typeof raw.value === 'string') raw = raw.value
  if (typeof raw !== 'string') return ''
  const tags = raw.match(/<\/?(strong|b|em|i|u|s|del|ins|a|h[1-6]|ul|ol|li|blockquote|code|sup|sub)\b[^>]*>/gi) ?? []
  return tags.map(t => {
    const name = t.toLowerCase().match(/^<\/?([a-z0-9]+)/)[1]
    const canon = { b: 'strong', i: 'em' }[name] ?? name
    const href = /^<a\b/i.test(t) ? (t.match(/href="([^"]*)"/i)?.[1] ?? '') : ''
    return `${t.startsWith('</') ? '/' : ''}${canon}${href ? `[${href}]` : ''}`
  }).join(',')
}

const isImageLike = (v) => isObj(v) && ('mid' in v || 'fid' in v || 'mediaUuid' in v
  || (typeof v.url === 'string' && /\.(png|jpe?g|webp|gif|svg|avif)(\?|$)/i.test(v.url)))

const imageOf = (v) => (isImageLike(v) ? { url: typeof v.url === 'string' ? v.url : null, alt: String(v.alt ?? '') } : null)

/** Résumé court d'une valeur quelconque (une ligne de liste, un objet). */
const summarize = (value) => {
  if (value == null || value === '') return ''
  const text = plainText(value)
  if (text != null) return text
  if (typeof value !== 'object') return String(value)
  if (Array.isArray(value)) return `${value.length} élément${value.length > 1 ? 's' : ''}`
  if (isImageLike(value)) return value.alt ? `Image « ${value.alt} »` : 'Image'
  // La première valeur textuelle de l'objet le nomme le mieux (le titre d'une
  // carte, l'intitulé d'un lien).
  for (const v of Object.values(value)) {
    const t = typeof v === 'string' ? plainText(v) : null
    if (t) return t
  }
  return ''
}

/**
 * Différence MOT À MOT de deux textes : `[{op: 'same'|'del'|'ins', text}]`.
 *
 * Plus longue sous-suite commune sur les mots (espaces conservés comme
 * jetons). Quadratique : au-delà d'un plafond on rend « tout supprimé / tout
 * ajouté » plutôt que de geler l'onglet sur un très long texte.
 */
export const diffWords = (before, after) => {
  const a = String(before ?? '').split(/(\s+)/).filter(t => t !== '')
  const b = String(after ?? '').split(/(\s+)/).filter(t => t !== '')
  if (a.length * b.length > 250000) {
    return [
      ...(a.length ? [{ op: 'del', text: a.join('') }] : []),
      ...(b.length ? [{ op: 'ins', text: b.join('') }] : []),
    ]
  }
  const n = a.length
  const m = b.length
  const lcs = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1))
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1])
    }
  }
  const out = []
  const push = (op, text) => {
    const last = out[out.length - 1]
    if (last && last.op === op) last.text += text
    else out.push({ op, text })
  }
  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (a[i] === b[j]) { push('same', a[i]); i++; j++ }
    else if (lcs[i + 1][j] >= lcs[i][j + 1]) { push('del', a[i]); i++ }
    else { push('ins', b[j]); j++ }
  }
  while (i < n) push('del', a[i++])
  while (j < m) push('ins', b[j++])

  // Lisibilité. La sous-suite commune s'accroche à tout ce qui se répète —
  // espaces, « & », « la » — et entrelace alors deux phrases sans rapport
  // (« TestTexte &réécrit… »). Deux corrections :
  //  - textes trop différents (moins de la moitié des mots en commun) : on
  //    rend « tout supprimé / tout ajouté », c'est ce qui s'est passé ;
  //  - sinon, entre deux changements, un « inchangé » qui n'est que de
  //    l'espace est absorbé : chaque zone modifiée se lit d'un bloc, l'ancien
  //    texte puis le nouveau.
  const words = (tokens) => tokens.filter(t => /\S/.test(t)).length
  const kept = out.filter(p => p.op === 'same').reduce((sum, p) => sum + words(p.text.split(/(\s+)/)), 0)
  if (kept < Math.max(words(a), words(b)) / 2) {
    return [
      ...(a.length ? [{ op: 'del', text: a.join('') }] : []),
      ...(b.length ? [{ op: 'ins', text: b.join('') }] : []),
    ]
  }
  const grouped = []
  let del = ''
  let ins = ''
  const flush = () => {
    if (del) grouped.push({ op: 'del', text: del })
    if (ins) grouped.push({ op: 'ins', text: ins })
    del = ''
    ins = ''
  }
  out.forEach((p, k) => {
    const between = k > 0 && k < out.length - 1 && out[k - 1].op !== 'same' && out[k + 1].op !== 'same'
    if (p.op === 'same' && !(between && !/\S/.test(p.text))) {
      flush()
      grouped.push(p)
    } else if (p.op === 'same') {
      del += p.text
      ins += p.text
    } else if (p.op === 'del') del += p.text
    else ins += p.text
  })
  flush()
  return grouped
}

// ---------------------------------------------------------------------------
// Libellés des champs
// ---------------------------------------------------------------------------

const humanize = (key) => {
  const spaced = String(key).replace(/(?<!^)([A-Z])/g, ' $1').replace(/[_\-.]+/g, ' ').trim()
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase() : String(key)
}

/**
 * `{ 'champ' | 'liste.colonne' → libellé }`, lu dans la déf telle que le
 * registre la sert (rjBuild DÉPLIÉ par `expandEditable`) : les libellés que
 * l'auteur du bloc a déclarés dans ses `editable:` — ceux que l'éditeur voit
 * déjà au survol — plus ceux des réglages du bloc (`settings:`).
 */
export const fieldLabels = (def) => {
  const labels = {}
  const visit = (node) => {
    if (Array.isArray(node)) { node.forEach(visit); return }
    if (!isObj(node)) return
    const attrs = node.attributes
    if (isObj(attrs) && typeof attrs['data-builder-field'] === 'string') {
      try {
        const opts = JSON.parse(attrs['data-builder-options'] ?? '{}')
        if (opts?.label && !labels[attrs['data-builder-field']]) labels[attrs['data-builder-field']] = String(opts.label)
      } catch { /* options illisibles : le libellé retombera sur la clé */ }
    }
    if (node.type === 'BuilderRowField' && node.field && node.column && node.options?.label) {
      labels[`${node.field}.${node.column}`] ??= String(node.options.label)
    }
    for (const v of Object.values(node)) if (typeof v === 'object') visit(v)
  }
  visit(def?.rjBuild?.renderView ?? def?.rjBuild)
  for (const s of def?.settings ?? []) if (s?.field && s.label) labels[s.field] ??= String(s.label)
  return labels
}

// ---------------------------------------------------------------------------
// Différences de contenu d'un bloc
// ---------------------------------------------------------------------------

/**
 * Les changements FEUILLES entre deux valeurs, chacun avec son chemin
 * (`['items', 2, 'title']`). Trois formes :
 *  - `text`  : deux textes (diff mot à mot à l'affichage) ;
 *  - `image` : deux images ;
 *  - `value` : tout le reste, résumé ;
 *  - `row`   : une ligne de liste ajoutée / retirée (`status`).
 * Les listes sont comparées ligne à ligne par position : une liste éditée en
 * place n'a pas d'identifiant de ligne.
 */
const leafChanges = (before, after, path = []) => {
  if (sameValue(before, after)) return []
  const tb = plainText(before)
  const ta = plainText(after)
  if ((tb != null || before == null) && (ta != null || after == null) && (tb != null || ta != null)) {
    // Même texte, écrit autrement : espace insécable, retours, HTML
    // re-sérialisé par l'éditeur riche (`<p>` ajouté, attributs réordonnés)
    // sans que l'éditeur ait rien changé. Ce n'est une modification que si la
    // MISE EN FORME a changé (gras, lien, titre, liste…).
    if (normText(tb) === normText(ta)) {
      return markup(before) === markup(after)
        ? []
        : [{ path, kind: 'format', before: tb ?? '', after: ta ?? '' }]
    }
    return [{ path, kind: 'text', before: tb ?? '', after: ta ?? '' }]
  }
  if (isImageLike(before) || isImageLike(after)) {
    return [{ path, kind: 'image', before: imageOf(before), after: imageOf(after) }]
  }
  if (Array.isArray(before) || Array.isArray(after)) {
    const a = Array.isArray(before) ? before : []
    const b = Array.isArray(after) ? after : []
    const out = []
    for (let i = 0; i < Math.max(a.length, b.length); i++) {
      if (i >= a.length) out.push({ path: [...path, i], kind: 'row', status: 'added', summary: summarize(b[i]) })
      else if (i >= b.length) out.push({ path: [...path, i], kind: 'row', status: 'removed', summary: summarize(a[i]) })
      else out.push(...leafChanges(a[i], b[i], [...path, i]))
    }
    return out
  }
  if (isObj(before) && isObj(after)) {
    const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])]
    return keys.flatMap(k => leafChanges(before[k], after[k], [...path, k]))
  }
  return [{ path, kind: 'value', before: summarize(before), after: summarize(after) }]
}

/** « Cartes › ligne 3 › Titre » */
const pathLabel = (path, labels) => {
  const [top, ...rest] = path
  const parts = [labels[top] ?? humanize(top)]
  let listKey = top
  for (const seg of rest) {
    if (typeof seg === 'number') parts.push(`ligne ${seg + 1}`)
    else {
      parts.push(labels[`${listKey}.${seg}`] ?? humanize(seg))
      listKey = `${listKey}.${seg}`
    }
  }
  return parts.join(' › ')
}

/**
 * Les changements d'un bloc présent des deux côtés, prêts à afficher, plus
 * leurs cibles dans le rendu (ce qui y est surligné).
 */
const blocChanges = (from, to, def) => {
  const labels = fieldLabels(def)
  // Les valeurs EFFECTIVES, comme le canevas les rend (BuilderBlockMount :
  // `{...rjBuild.data, ...bloc.data}`). Un bloc jamais retouché ne porte que
  // quelques clés ; le premier enregistrement de sa DataSync écrit en clair
  // toutes les autres, avec leur valeur par défaut. Comparées à « absent »,
  // elles passaient pour une vingtaine de modifications (vu sur un bloc
  // Contact : intitulés, message d'erreur…) et noyaient la seule vraie.
  // Même amorçage que BuilderBlockMount : défauts structurels du rjBuild,
  // puis `defaultData` de la déf (c'est lui qui porte le menu d'un en-tête,
  // les cartes d'une grille…), puis la donnée de l'instance.
  const defaults = {
    ...(isObj(def?.rjBuild?.data) ? def.rjBuild.data : {}),
    ...(isObj(def?.defaultData) ? def.defaultData : {}),
  }
  const effective = (data) => ({ ...defaults, ...(isObj(data) ? data : {}) })
  const changes = []
  if (from.type !== to.type) {
    changes.push({ label: 'Type de bloc', kind: 'value', before: from.type ?? '', after: to.type ?? '' })
  }
  const visibility = (list) => (Array.isArray(list) && list.length ? `Masqué sur : ${list.join(', ')}` : 'Visible partout')
  if (!sameValue([...(from.hiddenOn ?? [])].sort(), [...(to.hiddenOn ?? [])].sort())) {
    changes.push({ label: 'Visibilité', kind: 'value', before: visibility(from.hiddenOn), after: visibility(to.hiddenOn) })
  }
  const leaves = leafChanges(effective(from.data), effective(to.data))
  for (const leaf of leaves) changes.push({ ...leaf, label: leaf.path.length ? pathLabel(leaf.path, labels) : 'Contenu' })
  // Où le surligner dans le rendu : le champ (`data-builder-field`), et pour
  // une cellule de liste sa position (`data-builder-row-path`, que pose
  // BuilderRowField) — sinon c'est toute la liste qui s'allumerait pour un mot.
  const targets = leaves
    .filter(l => l.path.length > 0)
    .map(l => ({
      field: String(l.path[0]),
      rowPath: typeof l.path[1] === 'number' ? l.path.slice(1).join('.') : null,
      wholeRow: l.kind === 'row',
    }))
  return { changes, targets }
}

// ---------------------------------------------------------------------------
// Comparaison de deux pages
// ---------------------------------------------------------------------------

/**
 * Indices (dans `seq`) d'une plus longue sous-suite croissante — les blocs
 * communs qui n'ont PAS bougé les uns par rapport aux autres. Ceux qui n'en
 * font pas partie sont les blocs déplacés : c'est le plus petit ensemble de
 * déplacements qui explique le nouvel ordre (déplacer un bloc en tête ne
 * marque pas tous les autres comme « déplacés »).
 */
const longestIncreasing = (seq) => {
  const tails = []
  const prev = new Array(seq.length).fill(-1)
  for (let i = 0; i < seq.length; i++) {
    let lo = 0
    let hi = tails.length
    while (lo < hi) {
      const mid = (lo + hi) >> 1
      if (seq[tails[mid]] < seq[i]) lo = mid + 1
      else hi = mid
    }
    if (lo > 0) prev[i] = tails[lo - 1]
    tails[lo] = i
  }
  const keep = new Set()
  for (let k = tails.length ? tails[tails.length - 1] : -1; k >= 0; k = prev[k]) keep.add(k)
  return keep
}

/**
 * Met deux états de page en regard.
 *
 * Renvoie `{entries, stats, pageChanges}` :
 *  - `entries` : la page FUSIONNÉE, dans l'ordre de `to` ; chaque bloc retiré
 *    est réinséré juste après le bloc qui le précédait dans `from` (là où
 *    l'œil le cherche). Une entrée : `{key, status, moved, from, to, changes,
 *    targets}`, `status` ∈ `added | removed | modified | same` ;
 *  - `stats` : le décompte par statut (+ `moved`) ;
 *  - `pageChanges` : titre et métadonnées de la page.
 *
 * `registryByType` sert à nommer les champs (libellés des déclarations
 * `editable:` de la déf).
 */
export const comparePages = (fromPage, toPage, registryByType = {}) => {
  const fromBlocs = fromPage?.blocs ?? []
  const toBlocs = toPage?.blocs ?? []
  const fromIndex = new Map(fromBlocs.map((b, i) => [b.id, i]))
  const toIds = new Set(toBlocs.map(b => b.id))

  const common = toBlocs.filter(b => fromIndex.has(b.id))
  const stable = longestIncreasing(common.map(b => fromIndex.get(b.id)))
  const movedIds = new Set(common.filter((_, i) => !stable.has(i)).map(b => b.id))

  // Les blocs retirés, rangés derrière leur voisin d'avant encore présent.
  const removedAfter = new Map()
  let anchor = null
  for (const b of fromBlocs) {
    if (toIds.has(b.id)) { anchor = b.id; continue }
    if (!removedAfter.has(anchor)) removedAfter.set(anchor, [])
    removedAfter.get(anchor).push(b)
  }
  const removedEntry = (b) => ({ key: `from:${b.id}`, status: 'removed', moved: false, from: b, to: null, changes: [], targets: [] })

  const entries = [...(removedAfter.get(null) ?? []).map(removedEntry)]
  for (const b of toBlocs) {
    const before = fromIndex.has(b.id) ? fromBlocs[fromIndex.get(b.id)] : null
    if (!before) {
      entries.push({ key: `to:${b.id}`, status: 'added', moved: false, from: null, to: b, changes: [], targets: [] })
    } else {
      const { changes, targets } = blocChanges(before, b, registryByType[b.type] ?? registryByType[before.type])
      entries.push({
        key: `to:${b.id}`,
        status: changes.length ? 'modified' : 'same',
        moved: movedIds.has(b.id),
        from: before,
        to: b,
        changes,
        targets,
      })
    }
    entries.push(...(removedAfter.get(b.id) ?? []).map(removedEntry))
  }

  const stats = { added: 0, removed: 0, modified: 0, same: 0, moved: movedIds.size }
  for (const e of entries) stats[e.status]++

  const pageChanges = []
  if ((fromPage?.title ?? '') !== (toPage?.title ?? '')) {
    pageChanges.push({ label: 'Titre de la page', kind: 'text', before: fromPage?.title ?? '', after: toPage?.title ?? '' })
  }
  const META_LABELS = { title: 'Titre (référencement)', description: 'Description', image: 'Image de partage', robots: 'Robots', canonical: 'Adresse canonique' }
  for (const leaf of leafChanges(fromPage?.meta ?? {}, toPage?.meta ?? {})) {
    const key = leaf.path[0]
    pageChanges.push({ ...leaf, label: `Métadonnées › ${META_LABELS[key] ?? humanize(key ?? '')}` })
  }

  return { entries, stats, pageChanges }
}

// ---------------------------------------------------------------------------
// Choix de versionner (header)
// ---------------------------------------------------------------------------

const storageKey = (pageNid) => `orbit.builder.versioning.${pageNid}`

/**
 * Le choix « Créer une version » de l'éditeur, et le commentaire de la
 * prochaine version.
 *
 * Le choix est retenu PAR PAGE dans ce navigateur (une commodité : c'est
 * l'éditeur qui décide, page par page, s'il versionne son travail) ; le
 * commentaire, lui, ne vaut que pour la prochaine version.
 */
export const useVersioningChoice = (pageNid) => {
  const [enabled, setEnabledState] = useState(false)
  const [comment, setComment] = useState('')
  useEffect(() => {
    if (!pageNid) return
    try { setEnabledState(window.localStorage.getItem(storageKey(pageNid)) === '1') } catch { /* stockage indisponible */ }
  }, [pageNid])
  const setEnabled = useCallback((on) => {
    setEnabledState(on)
    if (!on) setComment('')
    try { window.localStorage.setItem(storageKey(pageNid), on ? '1' : '0') } catch { /* stockage indisponible */ }
  }, [pageNid])
  return { enabled, setEnabled, comment, setComment }
}
